import Backbone from 'backbone';
import { View } from 'backbone.marionette';
import Syphon from 'backbone.syphon';
import _ from 'lodash';

import ButtonCell from '@/backgrid/ButtonCell';
import GridView from '@/views/GridView';
import PageableCollection from '@/backbone/PageableCollection';
import StringCell from '@/backgrid/StringCell';
import showModal from '@/util/showModal';
import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';

import ReloadViewModule from '../../../services/ReloadViewModule';
import SaveCustomerOrderDevices from '../../../services/SaveCustomerOrderDevices';
import MFDDeviceEditModalView from '../../../views/MFDDeviceEditModalView';
import GetAllMFDCustomerOrderFormDataByOrderId from '../../../services/GetAllMFDCustomerOrderFormDataByOrderId';
import OrderDeviceSectionView from '../../../views/OrderDeviceSectionView';

import './MFDFormView.css';

import tmplMFDFormView from './MFDFormView.hbs';

const MFDFormView = View.extend({
  template: tmplMFDFormView,

  className: 'mfd-form-view',

  ui: {
    volumeBandDropdown: '#volumeBandDropdown',
    colorDropdown: '#colorDropdown',
    niprSiprDropdown: '#niprSiprDropdown',
    optionalAccessoriesDropdown: '#optionalAccessoriesDropdown',
    additionalServicesDropdown: '#additionalServicesDropdown',
    deviceQuantity: '#deviceQuantity',
    devicesAddSection: '.devices-add-section',
    devicesGridContainer: '.devices-grid-container',
    btnAdd: '.btn-add',
    btnReset: '.btn-reset',
    quantityAlert: '.quantity-error',
    colorAlert: '.color-error',
    niprAlert: '.nipr-error',
    noDevicesAlert: '.no-devices-error',
    descriptionSection: '.description-section'
  },

  regions: {
    devicesAddSection: '@ui.devicesAddSection',
    devicesGridContainer: '@ui.devicesGridContainer',
    descriptionSection: '@ui.descriptionSection'
  },

  events: {
    'click @ui.btnAdd': 'addMFDDevice',
    'click @ui.btnReset': 'onClickReset'
  },

  onRender() {
    const self = this;
    const isReadOnly = this.model.get('orderDetails').isReadOnly;

    this.devicesGridContainer = new GridView({
      columns: [
        {
          name: 'deleteMFDDevice',
          label: '',
          ariaLabel: 'Delete Device',
          sortable: false,
          headerCell: 'custom',
          cell: ButtonCell.extend({
            labelClass: 'fa fa-minus',
            buttonClass: 'btn-sm delete-mfd-device',
            onClick: this.onClickDeleteRow.bind(this)
          }),
          width: 4
        },
        {
          name: 'volumeBand',
          label: 'Volume Band',
          cell: StringCell,
          headerCell: 'custom',
          width: 12,
          editable: false,
          sortable: true
        },
        {
          name: 'isColor',
          label: 'Color',
          cell: StringCell,
          headerCell: 'custom',
          width: 6,
          editable: false,
          sortable: true
        },
        {
          name: 'isNipr',
          label: 'NIPR/SIPR',
          cell: StringCell,
          headerCell: 'custom',
          width: 10,
          editable: false,
          sortable: true
        },
        {
          name: 'optionalAccessories',
          label: 'Optional Accessories',
          cell: StringCell,
          headerCell: 'custom',
          width: 18,
          editable: false,
          sortable: true
        },
        {
          name: 'additionalServices',
          label: 'Additional Services',
          cell: StringCell,
          headerCell: 'custom',
          width: 18,
          editable: false,
          sortable: true
        },
        {
          name: 'quantity',
          label: 'Quantity',
          cell: StringCell,
          headerCell: 'custom',
          width: 10,
          editable: false,
          sortable: true
        },
        {
          name: 'paperHandlingCapacity',
          label: 'Description',
          cell: StringCell,
          headerCell: 'custom',
          width: 15,
          editable: false,
          sortable: true
        },
        {
          name: 'editDevice',
          label: '',
          editable: false,
          sortable: false,
          width: 10,
          cell: ButtonCell.extend({
            buttonClass: 'btn btn-sm btn-outline-primary text-center',
            name() {
              if (isReadOnly) {
                return 'View';
              }
              return 'Edit';
            },
            onClick(e, model) {
              isReadOnly ? self.onClickView(e, model, isReadOnly) : self.onClickEdit(e, model);
            }
          })
        }
      ],
      collection: new PageableCollection(this.model.get('orderItems'), { pageSize: 25 }),
      emptyText: 'No Devices'
    });
    this.showChildView('devicesGridContainer', this.devicesGridContainer);

    this.showChildView(
      'descriptionSection',
      new OrderDeviceSectionView({
        model: new Backbone.Model({
          title: 'Device Information',
          type: 'description',
          id: 'description',
          contract: 'MFD'
        })
      })
    );
  },

  saveRowItem(modalData, model) {
    model.set('volumeBand', modalData.volumeBand);
    model.set('isColor', modalData.isColor);
    model.set('isNipr', modalData.isNipr);
    model.set('optionalAccessories', modalData.optionalAccessories);
    model.set('additionalServices', modalData.additionalServices);
    model.set('quantity', modalData.quantity);
    model.set('paperHandlingCapacity', modalData.description);
  },

  async onClickEdit(e, model) {
    e.preventDefault();
    const device = model.attributes;
    const modalData = await showModal({
      title: 'Edit Device Information',
      view: new MFDDeviceEditModalView({
        model: new Backbone.Model({ device })
      }),
      dialogClasses: 'modal-lg'
    });
    this.saveRowItem(modalData, model);
  },

  async onClickView(e, model, isReadOnly) {
    e.preventDefault();
    const device = model.attributes;
    await showModal({
      title: 'View Device Information',
      view: new MFDDeviceEditModalView({
        model: new Backbone.Model({ device, isReadOnly })
      }),
      dialogClasses: 'modal-lg'
    });
  },

  addMFDDevice() {
    const formData = Syphon.serialize(this.ui.devicesAddSection);
    this.devicesGridContainer.collection.add({
      volumeBand: formData.volumeBand,
      isColor: formData.isColor,
      isNipr: formData.isNipr,
      optionalAccessories: formData.optionalAccessories,
      additionalServices: formData.additionalServices,
      quantity: formData.deviceQuantity,
      paperHandlingCapacity: formData.description
    });
  },

  onClickReset(e) {
    e.preventDefault();
    this.ui.volumeBandDropdown.val('');
    this.ui.colorDropdown.val('');
    this.ui.niprSiprDropdown.val('');
    this.ui.optionalAccessoriesDropdown.val('');
    this.ui.additionalServicesDropdown.val('');
    this.ui.deviceQuantity.val('');
  },

  onClickDeleteRow(e, model) {
    e.preventDefault();
    this.devicesGridContainer.collection.remove(model);
  },

  showSaveError() {
    showErrorMessage({
      errorCode: 'saveError',
      errorTitle: 'Save Error',
      errorMessage:
        'An error occurred while saving devices for this order. Try again or contact an administrator if your issue persists.'
    });
  },

  showSaveSuccess() {
    showSuccessMessage({
      code: 'saveSuccess',
      title: 'Save Success',
      message: 'This order has been saved successfully.',
      hideAfter: 5
    });
  },

  convertColor(item) {
    if (item.isColor) {
      item.isColor = item.isColor === 'Mono (B&W)' ? 0 : 1;
    }
    return item;
  },

  convertNipr(item) {
    if (item.isNipr) {
      item.isNipr = item.isNipr === 'NIPR' ? 1 : 0;
    }
    return item;
  },

  sumFilter(data, filter) {
    return _.reduce(
      _.filter(data, filter),
      (sum, n) => {
        return parseInt(sum) + parseInt(n.quantity);
      },
      0
    );
  },

  getDeviceSummaryQuantities(data) {
    return {
      monoNiprQuantity: this.sumFilter(data, { isColor: 0, isNipr: 1 }),
      monoSiprQuantity: this.sumFilter(data, { isColor: 0, isNipr: 0 }),
      monoQuantity: this.sumFilter(data, ['isColor', 0]),
      colorNiprQuantity: this.sumFilter(data, { isColor: 1, isNipr: 1 }),
      colorSiprQuantity: this.sumFilter(data, { isColor: 1, isNipr: 0 }),
      colorQuantity: this.sumFilter(data, ['isColor', 1])
    };
  },

  async refreshGridIds() {
    const orderId = this.model.get('orderDetails')?.orderId;
    const data = await GetAllMFDCustomerOrderFormDataByOrderId(orderId);
    _.each(this.devicesGridContainer.collection.models, (model, i) => {
      if (!model.get('id')) model.set('id', data[i]?.id);
      if (!model.get('mfdOrderLineItemId')) model.set('mfdOrderLineItemId', data[i]?.mfdOrderLineItemId);
    });
  },

  validate() {
    const gridData = this.devicesGridContainer.collection.toJSON();
    const colorGridData = _.map(gridData, this.convertColor);
        const devicesFormData = _.map(colorGridData, this.convertNipr);
    let isValid = true;
    if (!devicesFormData.length) {
      this.ui.noDevicesAlert.show();
      this.trigger('has:errors');
      return false;
    }
    _.each(devicesFormData, (item) => {
      if (!item.quantity || item?.quantity === '0') {
        isValid = false;
        this.ui.quantityAlert.show();
      }
      if (item.isColor === '') {
        isValid = false;
        this.ui.colorAlert.show();
      }
      if (item.isNipr === '') {
        isValid = false;
        this.ui.niprAlert.show();
      }
    });
    isValid ? this.trigger('no:errors') : this.trigger('has:errors');
    return isValid;
  },

  hideErrors() {
    this.ui.quantityAlert.hide();
    this.ui.colorAlert.hide();
    this.ui.niprAlert.hide();
    this.ui.noDevicesAlert.hide();
  },

  async onSave() {
    this.hideErrors();

    const gridData = this.devicesGridContainer.collection.toJSON();
    const colorGridData = _.map(gridData, this.convertColor);
    const devicesFormData = _.map(colorGridData, this.convertNipr);

    if (!this.validate()) return;

    // Set CLINs - pad start with 0s
    _.each(devicesFormData, (item, i) => {
      item.clin = _.padStart(parseInt(i + 1), 4, '0');
      return item;
    });

    const orderFormData = this.getDeviceSummaryQuantities(devicesFormData);

    const formData = {
      devicesFormData,
      orderFormData,
      orderId: this.model.get('orderDetails').orderId,
      orderTypeId: this.model.get('orderDetails').orderTypeId,
      itPocExists: true
    };

    try {
      await SaveCustomerOrderDevices(formData);
      await this.refreshGridIds();
      await ReloadViewModule.reload();
      this.showSaveSuccess();
    } catch (err) {
      this.showSaveError();
      throw new Error(err);
    }
  }
});

export default MFDFormView;
